'use client';
import { Button, Drawer, Flex, Link, Skeleton } from '@skin-seoul/design-system';
import {
  TEMP_ITEM_KEY,
  useCartService,
  useGetAmountToFreeShipping,
  useGetShippingPolicy,
  useRegionCurrency,
  type Currency,
  CLASSNAME,
  getFilteredHalloweenPromoItems,
  getCartSummary,
} from '@skin-seoul/service';
import { BridgeApi, Condition } from '@skin-seoul/utils';
import React from 'react';
import { BMSMCampaignBanner } from '../../../bmsm-campaign/BMSMCampaignBanner';
import QueryBoundary from '../../../utils/react/QueryBoundary';
import { MiniCartBag } from '../components/CardCountBag';
import CartOrderSummary from '../components/CartOrderSummary';
import CartProductCard from '../components/CartProductCard';
import FreeShippingProgress from '../components/FreeShippingProgress';

type CartWithItemViewProps = {
  currency: Currency;

  ImageComponent?: React.ComponentType<{ src: string; alt: string; width: number; height: number }>;
};

export const CartWithItemView = ({ currency, ImageComponent }: CartWithItemViewProps) => {
  const { useGetCartSuspenseQuery, useDeleteCartItem, useUpdateCartItem } = useCartService();

  const { data } = useGetCartSuspenseQuery({ currency });
  const { country } = useRegionCurrency();
  const cartData = data?.cart;
  const { mutate: deleteItemMutate } = useDeleteCartItem({ currency });
  const { mutate: updateItemMutate } = useUpdateCartItem({ currency });

  const { getAmountToFreeShipping } = useGetAmountToFreeShipping(country);
  const { shippingPolicy } = useGetShippingPolicy(country);
  const shippingCost = cartData.shippingRates?.length > 0 ? cartData.shippingRates[0].price : 0;

  const { items, filteredCoupons, orderTotal, discountTotal, totalTax, isFreeShipping } = getCartSummary(cartData);

  const filteredItems = getFilteredHalloweenPromoItems(items);
  const totalProductPrice = filteredItems.reduce((prev, curr) => prev + curr.price.price * curr.quantity, 0);
  const totalProductQuantity = filteredItems.reduce((prev, curr) => prev + curr.quantity, 0);

  const handleDeleteItem = (itemKey: string) => {
    deleteItemMutate(
      {
        itemKey,
        currency,
      },
      {
        onSuccess: () => {
          BridgeApi.cartUpdated();
        },
      },
    );
  };

  const handleUpdateCartItem = ({ quantity, itemKey }: { quantity: number; itemKey: string }) => {
    updateItemMutate(
      {
        itemKey,
        quantity,
        currency,
      },
      {
        onSuccess: () => {
          BridgeApi.cartUpdated();
        },
      },
    );
  };

  return (
    <>
      <Drawer.Header
        alignItems="center"
        backgroundColor="neutral.50"
        display="flex"
        height="72px"
        flexDirection="row"
        justifyContent="space-between"
        padding="10px"
        width="100%"
        paddingRight="20px"
      >
        <Drawer.Title fontWeight="bold" textAlign="center" textStyle="paragraph.medium" width="100%">
          Shopping Cart
        </Drawer.Title>

        <Flex alignItems="center" justifyContent="center" marginLeft="auto" position="absolute" right={4} width="40px">
          <MiniCartBag quantity={totalProductQuantity} />
        </Flex>
      </Drawer.Header>

      <Drawer.Body padding="0" width="100%" overflowX="auto" paddingX="12px">
        <Flex flexDirection="column" height="100%" width="100%">
          <QueryBoundary clientOnly loadingFallback={<Skeleton height="130px" width="100%" />}>
            <BMSMCampaignBanner />
          </QueryBoundary>
          {[...filteredItems].reverse().map((item, index) => (
            <React.Fragment key={item.key}>
              <Condition
                expression={item.name === TEMP_ITEM_KEY}
                then={<CartProductCard.Skeleton />}
                else={
                  <CartProductCard
                    item={item}
                    currency={cartData.currency.currency}
                    onQuantityChange={(quantity) => {
                      handleUpdateCartItem({
                        itemKey: item.key,
                        quantity: quantity,
                      });
                    }}
                    onRemoveItem={() => handleDeleteItem(item.key)}
                    ImageComponent={ImageComponent}
                  />
                }
              />

              {index < filteredItems.length - 1 && (
                <Flex borderBottom="2px solid" borderColor="neutral.100" width="100%" />
              )}
            </React.Fragment>
          ))}
        </Flex>
      </Drawer.Body>
      <Flex
        backgroundColor="neutral.50"
        flexDirection="column"
        gap={5}
        height="fit-content"
        padding="16px"
        width="100%"
      >
        <Flex position="relative" flexDirection="column" gap={5}>
          <FreeShippingProgress
            currency={currency}
            threshold={shippingPolicy?.freeShippingThreshold ?? 1}
            isFreeShipping={isFreeShipping}
            amountToFreeShipping={getAmountToFreeShipping(totalProductPrice)}
          />
          <CartOrderSummary
            subtotal={totalProductPrice ? totalProductPrice : 0}
            deliveryFee={shippingCost}
            totalPrice={orderTotal}
            totalDiscount={discountTotal}
            totalTax={totalTax}
            country={country}
            coupons={filteredCoupons}
            isFreeShipping={shippingCost === 0}
          />
        </Flex>

        <Button
          asChild
          backgroundColor="primary.600"
          borderRadius="8px"
          size="lg"
          textTransform="uppercase"
          width="100%"
          className={CLASSNAME.NOT_TRANSLATE}
          _hover={{ backgroundColor: 'primary.400' }}
          _active={{ backgroundColor: 'primary.400' }}
        >
          <Link unstyled href="/checkout/">
            CHECKOUT
          </Link>
        </Button>
      </Flex>
    </>
  );
};
